import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const VERSION_CHECK_INTERVAL = 300000; // Check every 5 minutes
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feature flag - set to true to enable auto-refresh
// IMPORTANT: Only enable this after setting BUILD_VERSION in your CI/CD pipeline
const ENABLE_VERSION_CHECK = false;

/**
 * Hook to detect new app deployments and auto-refresh
 * 
 * SETUP REQUIRED:
 * 1. Set BUILD_VERSION env var in your Azure CI/CD pipeline
 *    Example: BUILD_VERSION=$(git rev-parse --short HEAD)
 * 2. Change ENABLE_VERSION_CHECK to true in this file
 * 
 * Without BUILD_VERSION set, version defaults to '1.0.0' which is stable
 * but won't trigger auto-refresh on deployments.
 */
export const useVersionCheck = () => {
  const currentVersion = useRef(null);
  const isChecking = useRef(false);
  const hasRefreshed = useRef(false);

  const checkVersion = useCallback(async () => {
    // Feature disabled - do nothing
    if (!ENABLE_VERSION_CHECK) return;
    
    // Prevent concurrent checks or multiple refreshes
    if (isChecking.current || hasRefreshed.current) return;
    isChecking.current = true;

    try {
      const response = await fetch(`${API_URL}/api/version`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        isChecking.current = false;
        return;
      }

      const data = await response.json();
      const newVersion = data.version;

      // Skip if version is the default (BUILD_VERSION not configured)
      if (newVersion === '1.0.0') {
        console.debug('[VersionCheck] BUILD_VERSION not configured, skipping');
        isChecking.current = false;
        return;
      }

      if (currentVersion.current === null) {
        // First check - store the initial version
        currentVersion.current = newVersion;
        sessionStorage.setItem('app_version', newVersion);
        console.log('[VersionCheck] Initial version:', newVersion);
      } else if (currentVersion.current !== newVersion) {
        // Version changed - new deployment detected
        console.log('[VersionCheck] New version detected:', newVersion, 'Previous:', currentVersion.current);
        
        hasRefreshed.current = true;
        
        toast.info('A new version is available. Refreshing...', {
          duration: 2000,
        });

        setTimeout(() => {
          window.location.reload();
        }, 2500);
      }
    } catch (error) {
      console.debug('[VersionCheck] Check failed:', error.message);
    } finally {
      isChecking.current = false;
    }
  }, []);

  useEffect(() => {
    // Feature disabled - skip all setup
    if (!ENABLE_VERSION_CHECK) return;

    const storedVersion = sessionStorage.getItem('app_version');
    if (storedVersion) {
      currentVersion.current = storedVersion;
    }

    // Delay initial check to let the app fully load
    const initialCheckTimeout = setTimeout(checkVersion, 10000);

    // Set up periodic checks
    const intervalId = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    // Check when tab becomes visible (debounced)
    let visibilityTimeout = null;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(visibilityTimeout);
        visibilityTimeout = setTimeout(checkVersion, 5000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(initialCheckTimeout);
      clearTimeout(visibilityTimeout);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);

  return { checkVersion };
};

export default useVersionCheck;
